import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { chemistryApi } from '../../api/chemistry';
import { useChemistryStore } from '../../stores/chemistryStore';
import { useTaskStore } from '../../stores/taskStore';

interface MergeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  projectId?: number | null;
}

// Strip hex prefix (e.g. "288f816a_") from storage filenames
const stripHexPrefix = (name: string): string =>
  name.replace(/^[0-9a-f]{8}_/, '');

const MergeDialog: React.FC<MergeDialogProps> = ({ isOpen, onClose, onComplete, projectId }) => {
  const { files, selectedFileIds, clearSelection } = useChemistryStore();
  const { addTask, updateTask } = useTaskStore();

  const [mergeOption, setMergeOption] = useState<'all' | 'structures'>('all');
  const [removeDuplicates, setRemoveDuplicates] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedFiles = files.filter((f) => selectedFileIds.includes(f.id));

  // Only reset when dialog opens — use selectedFileIds (stable) not selectedFiles (new ref each render)
  useEffect(() => {
    if (isOpen && selectedFileIds.length > 0) {
      const first = files.find((f) => selectedFileIds.includes(f.id));
      if (first) {
        const displayName = first.originalFileName || stripHexPrefix(first.fileName);
        const baseName = displayName.replace(/\.[^/.]+$/, '');
        setNewFileName(`${baseName}_merged`);
      }
      setError(null);
      setIsStarting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Start polling for a task
  const startPolling = (taskId: string, fileName: string) => {
    const poll = async () => {
      try {
        const status = await chemistryApi.getMergeStatus(taskId);

        if (status.status === 'COMPLETED') {
          updateTask(taskId, { status: 'completed', percent: 100 });
          onComplete(); // Refresh file list
          return; // Stop polling
        } else if (status.status === 'ERROR') {
          updateTask(taskId, { status: 'error', error: status.error || 'Merge failed' });
          return; // Stop polling
        } else if (status.status === 'CANCELLED') {
          updateTask(taskId, { status: 'cancelled', percent: 0 });
          return; // Stop polling
        } else {
          // Update progress and total molecules
          updateTask(taskId, {
            percent: status.percent || 0,
            totalMolecules: status.totalMolecules || 0
          });
          setTimeout(poll, 1000); // Poll more frequently for better UX
        }
      } catch (err: any) {
        // 404 means task not ready yet, keep polling
        if (err.response?.status === 404) {
          setTimeout(poll, 1000);
        } else {
          updateTask(taskId, { status: 'error', error: err.message || 'Failed to check status' });
        }
      }
    };

    // Start polling immediately
    setTimeout(poll, 500);
  };

  const handleMerge = async () => {
    if (!newFileName.trim()) {
      setError('Please enter a file name');
      return;
    }

    setIsStarting(true);
    setError(null);

    try {
      const result = await chemistryApi.mergeFiles({
        fileIds: selectedFileIds,
        fileName: newFileName.trim(),
        withProperties: mergeOption === 'all',
        withDeduplication: removeDuplicates,
        ...(projectId ? { projectId } : {}),
      });

      // Add task to store
      addTask({
        id: result.taskId,
        type: 'merge',
        status: 'running',
        percent: 0,
        fileName: newFileName.trim(),
      });

      // Start background polling
      startPolling(result.taskId, newFileName.trim());

      // Close dialog and clear selection immediately
      clearSelection();
      onClose();
    } catch (err: any) {
      setIsStarting(false);
      setError(err.message || 'Failed to start merge');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsStarting(false);
    onClose();
  };

  const totalMolecules = selectedFiles.reduce(
    (sum, f) => sum + (f.moleculesLibraryCount || 0),
    0
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Merge Files" size="md">
      <div className="space-y-5">
        {/* Selected Files Summary */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600 mb-2">
            Merging {selectedFiles.length} files ({totalMolecules.toLocaleString()}{' '}
            molecules total)
          </div>
          <div className="max-h-24 overflow-y-auto space-y-1">
            {selectedFiles.map((file) => (
              <div key={file.id} className="text-sm text-gray-700 truncate">
                {file.originalFileName || stripHexPrefix(file.fileName)}
              </div>
            ))}
          </div>
        </div>

        {/* Merge Options */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            What to merge
          </label>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="mergeOption"
                value="all"
                checked={mergeOption === 'all'}
                onChange={() => setMergeOption('all')}
                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                disabled={isStarting}
              />
              <span className="text-sm text-gray-700">All columns</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="mergeOption"
                value="structures"
                checked={mergeOption === 'structures'}
                onChange={() => setMergeOption('structures')}
                className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                disabled={isStarting}
              />
              <span className="text-sm text-gray-700">Structures only</span>
            </label>
          </div>
        </div>

        {/* Remove Duplicates */}
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={removeDuplicates}
            onChange={(e) => setRemoveDuplicates(e.target.checked)}
            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
            disabled={isStarting}
          />
          <span className="text-sm text-gray-700">Remove duplicates during merge</span>
        </label>

        {/* New File Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New file name
          </label>
          <input
            type="text"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            placeholder="Enter file name"
            disabled={isStarting}
          />
        </div>

        {/* Info about background processing */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm">
          The merge will run in the background. You can continue working while it processes.
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            disabled={isStarting}
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={isStarting || selectedFiles.length < 2}
            className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-md hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isStarting ? 'Starting...' : 'Merge'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MergeDialog;
